/**
 * @fileoverview Preferences service using axios client and Zod validation.
 * @module features/preferences/api/preferencesService
 */

import apiClient from '@/lib/apiClient'
import { z } from 'zod'
import { OrgIdSchema, ProfileSchema, type Profile } from '@/features/orgs/schemas/orgSchemas'

/** Language tag schema (BCP 47-like minimal). */
const LanguageTagSchema = z.string().min(2)

/** Request schema for creating preferences. */
export const CreatePreferencesRequestSchema = z.object({
  preferredLanguage: LanguageTagSchema,
  defaultOrgId: OrgIdSchema.optional(),
})
export type CreatePreferencesRequest = z.infer<typeof CreatePreferencesRequestSchema>

/** Request schema for updating preferences. */
export const UpdatePreferencesRequestSchema = z
  .object({
    preferredLanguage: LanguageTagSchema.optional(),
    defaultOrgId: OrgIdSchema.optional(),
  })
  .refine(data => data.preferredLanguage !== undefined || data.defaultOrgId !== undefined, {
    message: 'At least one field must be provided',
  })
export type UpdatePreferencesRequest = z.infer<typeof UpdatePreferencesRequestSchema>

/** Minimal response schema from POST/PATCH preferences (backend may return partial). */
export const PreferencesResponseSchema = z
  .object({ preferredLanguage: LanguageTagSchema.optional(), defaultOrgId: OrgIdSchema.optional() })
  .passthrough()
export type PreferencesResponse = z.infer<typeof PreferencesResponseSchema>

/** Request schema for switching organization. */
export const SwitchOrgRequestSchema = z.object({
  organizationId: OrgIdSchema,
  preferredLanguage: LanguageTagSchema.optional(),
})
export type SwitchOrgRequest = z.infer<typeof SwitchOrgRequestSchema>

/**
 * Create initial preference record.
 * @param body CreatePreferencesRequest
 * @returns Minimal PreferencesResponse
 */
export async function createPreferences(
  body: CreatePreferencesRequest
): Promise<PreferencesResponse> {
  const safe = CreatePreferencesRequestSchema.parse(body)
  const res = await apiClient.post('/users/me/preferences', safe)
  return PreferencesResponseSchema.parse(res.data)
}

/**
 * Update language and/or default organization.
 * @param body UpdatePreferencesRequest
 * @returns Minimal PreferencesResponse
 */
export async function updatePreferences(
  body: UpdatePreferencesRequest
): Promise<PreferencesResponse> {
  const safe = UpdatePreferencesRequestSchema.parse(body)
  const res = await apiClient.patch('/users/me/preferences', safe)
  return PreferencesResponseSchema.parse(res.data)
}

/**
 * Switch active/default organization server-side (validates membership) and return enriched profile.
 * @param body SwitchOrgRequest
 * @returns Updated profile with organizations, defaultOrgId, preferredLanguage
 */
export async function switchOrg(body: SwitchOrgRequest): Promise<Profile> {
  const safe = SwitchOrgRequestSchema.parse(body)
  const res = await apiClient.post('/users/me/preferences/switch-org', safe)
  return ProfileSchema.parse(res.data)
}
